import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex flex-col">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 w-full">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <img 
              src="/logo-icon.png" 
              alt="SiteScope" 
              className="w-12 h-12 sm:w-12 sm:h-12 md:w-14 md:h-14 transition-transform group-hover:scale-105" 
            />
            <span className="text-2xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
              SiteScope
            </span>
          </Link>
          
          <Link 
            to="/" 
            className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16 w-full">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-10 shadow-lg border border-gray-200 dark:border-gray-700">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Terms of Service
          </h1>
          
          <div className="prose prose-blue dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
            <p className="text-lg">
              Welcome to SiteScope. By using our service, you agree to these terms. Please read them carefully.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">1. Service Description</h2>
            <p>
              SiteScope provides website monitoring and uptime tracking services. We check your websites at regular intervals 
              and notify you when issues are detected.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">2. Account Terms</h2>
            <ul className="space-y-2">
              <li>You must be 18 years or older to use this service</li>
              <li>You must provide a valid email address and accurate information</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You are responsible for all activities that occur under your account</li>
              <li>You may not use the service for any illegal purpose</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">3. Payment Terms</h2>
            <ul className="space-y-2">
              <li>All fees are in Philippine Pesos (₱) or US Dollars ($)</li>
              <li>Subscriptions automatically renew unless cancelled</li>
              <li>You can cancel your subscription at any time</li>
              <li>We offer a 30-day money-back guarantee on all paid plans</li>
              <li>Prices are subject to change with 30 days notice</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">4. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="space-y-2">
              <li>Monitor websites you don't own or have permission to monitor</li>
              <li>Use the service to perform DDoS attacks or overwhelm servers</li>
              <li>Attempt to reverse engineer or hack the service</li>
              <li>Share your account with others</li>
              <li>Resell the service without permission</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">5. Service Availability</h2>
            <p>
              We strive to provide 99.9% uptime for our service. However, we do not guarantee uninterrupted service. 
              We may perform maintenance that temporarily affects service availability.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">6. Data and Privacy</h2>
            <ul className="space-y-2">
              <li>We collect only the data necessary to provide our service</li>
              <li>We never sell your data to third parties</li>
              <li>See our <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">Privacy Policy</Link> for details</li>
              <li>You can export or delete your data at any time</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">7. Limitation of Liability</h2>
            <p>
              SiteScope is provided "as is" without warranties of any kind. We are not liable for:
            </p>
            <ul className="space-y-2">
              <li>Lost revenue or business due to website downtime</li>
              <li>Missed alerts or notifications</li>
              <li>Inaccurate monitoring data</li>
              <li>Service interruptions or errors</li>
            </ul>
            <p className="mt-4">
              Our total liability is limited to the amount you paid us in the past 12 months.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">8. Cancellation and Termination</h2>
            <ul className="space-y-2">
              <li>You can cancel your subscription at any time from your account settings</li>
              <li>We may terminate accounts that violate these terms</li>
              <li>Upon termination, your access to paid features ends</li>
              <li>You can downgrade to our free plan instead of canceling</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">9. Changes to Terms</h2>
            <p>
              We may update these terms from time to time. We'll notify you of significant changes via email. 
              Continued use of the service after changes constitutes acceptance of the new terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">10. Contact Us</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at{' '}
              <a href="mailto:support@sitescope.work" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                support@sitescope.work
              </a>
            </p>

            <div className="bg-gray-100 dark:bg-gray-700/50 p-6 rounded-lg mt-8">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                <strong>Last updated:</strong> November 7, 2025
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
